import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/editorStore';
import { CaptionCanvas } from './CaptionCanvas';
import { playbackClock } from '../playback/clock';

const ASPECTS: Record<string, { w: number; h: number }> = {
  '9:16': { w: 720, h: 1280 },
  '16:9': { w: 1280, h: 720 },
  '1:1': { w: 1000, h: 1000 },
  '4:5': { w: 1024, h: 1280 },
};

interface Props {
  aspect: string;
  showSafeZones: boolean;
}

/** The video preview + caption overlay, framed to the chosen aspect ratio. */
export function PreviewStage({ aspect, showSafeZones }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const words = useEditor((s) => s.words);
  const segments = useEditor((s) => s.segments);
  const style = useEditor((s) => s.resolvedStyle());
  const placement = useEditor((s) => s.resolvedPlacement());
  const styleForSegment = useEditor((s) => s.styleForSegment);
  const placementForSegment = useEditor((s) => s.placementForSegment);
  const videoUrl = useEditor((s) => s.videoUrl);
  const hooks = useEditor((s) => s.hooks);
  const durationMs = useEditor((s) => s.durationMs);
  const isBehind = useEditor((s) => s.template().renderMode === 'behind');

  // On-demand depth preview: a person cut-out of the CURRENT frame, drawn over
  // the caption so you can see the "behind the subject" effect without a full
  // export. Live per-frame segmentation is too slow, so this is a snapshot.
  const [depthUrl, setDepthUrl] = useState<string | null>(null);
  const [depthBusy, setDepthBusy] = useState(false);
  const [depthErr, setDepthErr] = useState<string | null>(null);

  const dims = ASPECTS[aspect] ?? ASPECTS['9:16']!;

  useEffect(() => {
    playbackClock.attachMedia(videoRef.current ?? null);
    playbackClock.setDuration(durationMs);
    return () => playbackClock.attachMedia(null);
  }, [videoUrl, durationMs]);

  // A depth snapshot is only valid for the frame it was taken on - clear it on
  // play/seek so it never lingers over a different frame.
  useEffect(() => {
    return playbackClock.subscribe(() => {
      setDepthUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    });
  }, []);

  const previewDepth = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setDepthErr('Load a video first.');
      return;
    }
    setDepthErr(null);
    setDepthBusy(true);
    try {
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      const cx = c.getContext('2d');
      if (!cx) throw new Error('no canvas');
      cx.drawImage(video, 0, 0);
      let blob: Blob;
      try {
        blob = await new Promise<Blob>((res, rej) =>
          c.toBlob((b) => (b ? res(b) : rej(new Error('capture failed'))), 'image/png'),
        );
      } catch {
        throw new Error(
          'This video is cross-origin; depth preview works right after upload. Export always applies depth.',
        );
      }
      const { removeBackground } = await import('@imgly/background-removal');
      const fg = await removeBackground(blob);
      const url = URL.createObjectURL(fg);
      setDepthUrl(url);
    } catch (e) {
      setDepthErr(e instanceof Error ? e.message : 'Depth preview failed');
    } finally {
      setDepthBusy(false);
    }
  };

  return (
    <div className="preview-stage" style={{ aspectRatio: `${dims.w} / ${dims.h}` }}>
      {videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          className="preview-video"
          playsInline
          muted={false}
          crossOrigin="anonymous"
        />
      ) : (
        <div className="preview-video preview-placeholder">
          <span>Preview - load a video to composite captions over footage</span>
        </div>
      )}
      <CaptionCanvas
        words={words}
        segments={segments}
        style={style}
        placement={placement}
        styleFor={(seg) => ({
          style: styleForSegment(seg),
          placement: placementForSegment(seg),
        })}
        hooks={hooks}
        width={dims.w}
        height={dims.h}
      />
      {/* Depth snapshot: the cut-out person drawn ON TOP of the caption. */}
      {depthUrl && <img className="depth-layer" src={depthUrl} alt="" aria-hidden />}

      {showSafeZones && (
        <div className="safe-zones" aria-hidden>
          <div className="safe top" />
          <div className="safe bottom" />
          <div className="safe right" />
        </div>
      )}

      <div className="depth-controls">
        <button onClick={previewDepth} disabled={depthBusy}>
          {depthBusy ? 'Segmenting…' : depthUrl ? 'Refresh depth' : '◐ Preview depth'}
        </button>
        {depthUrl && (
          <button
            className="depth-clear"
            onClick={() => {
              URL.revokeObjectURL(depthUrl);
              setDepthUrl(null);
            }}
          >
            Clear
          </button>
        )}
        <span className="depth-note">
          {depthErr ??
            (isBehind
              ? 'Depth is applied on export'
              : 'Pick a “Behind the Person” template to keep depth on export')}
        </span>
      </div>
    </div>
  );
}
